import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Loader2, Check, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMALS = [
    { index: 0, chinese: '子', english: 'Rat' },
    { index: 1, chinese: '丑', english: 'Ox' },
    { index: 2, chinese: '寅', english: 'Tiger' },
    { index: 3, chinese: '卯', english: 'Rabbit' },
    { index: 4, chinese: '辰', english: 'Dragon' },
    { index: 5, chinese: '巳', english: 'Snake' },
    { index: 6, chinese: '午', english: 'Horse' },
    { index: 7, chinese: '未', english: 'Goat' },
    { index: 8, chinese: '申', english: 'Monkey' },
    { index: 9, chinese: '酉', english: 'Rooster' },
    { index: 10, chinese: '戌', english: 'Dog' },
    { index: 11, chinese: '亥', english: 'Pig' },
];

const PRO_FEATURES = [
    "Full Monthly Energy Charts",
    "Personalized Date Selection",
    "Advanced BaZi Analysis",
    "Crimson Horse Insights",
    "Future Month Forecasts",
    "Unlimited Access",
    "Full Features",
];

const ProSignup = () => {
    const { t, language } = useLanguage();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [birthYear, setBirthYear] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [calculatedAnimal, setCalculatedAnimal] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState('pro_trial');

    useEffect(() => {
        if (birthYear && birthYear >= 1900 && birthYear <= 2100) {
            const index = (birthYear - 4) % 12;
            setCalculatedAnimal(ANIMALS[index]);
        } else {
            setCalculatedAnimal(null);
        }
    }, [birthYear]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        if (password !== confirmPassword) {
            setError(language === 'zh' ? '密码不匹配' : 'Passwords do not match');
            setLoading(false);
            return;
        }
        
        if (password.length < 6) {
            setError(language === 'zh' ? '密码至少需要6个字符' : 'Password must be at least 6 characters');
            setLoading(false);
            return;
        }
        
        try {
            const response = await axios.post(`${API_URL}/api/auth/register-and-pay`, {
                email,
                password,
                name,
                birth_year: birthYear ? parseInt(birthYear) : null,
                origin_url: window.location.origin
            });
            
            window.location.href = response.data.checkout_url;
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed');
            setLoading(false);
        }
    };

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 1920; y--) {
        years.push(y);
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
            <div className="w-full max-w-lg animate-fade-in">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <h1 className="font-serif text-2xl font-bold">
                            <span className="font-chinese text-wealth">通书</span> Online Tong Shu
                        </h1>
                    </Link>
                </div>
                
                <Card className="border-2">
                    <CardHeader className="space-y-1 pb-4">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-serif text-2xl">
                                {language === 'zh' ? '创建 PRO 账号' : 'Create PRO Account'}
                            </CardTitle>
                            <div className="text-right">
                                <span className="text-2xl font-bold text-slate-900">$1</span>
                                <span className="text-sm text-muted-foreground ml-1">/ 30 days</span>
                            </div>
                        </div>
                        <CardDescription>
                            {language === 'zh' 
                                ? '注册并立即获得 PRO 权限'
                                : 'Sign up and get instant PRO access'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-5 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                            <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">PRO includes:</p>
                            <div className="grid grid-cols-1 gap-1">
                                {PRO_FEATURES.map((f, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                                        <Check className="w-3 h-3 flex-shrink-0" />
                                        <span>{f}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-3">
                            {error && (
                                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm" data-testid="pro-signup-error">
                                    {error}
                                </div>
                            )}
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="name">{t('name')}</Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder={language === 'zh' ? '您的姓名' : 'Your name'}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        data-testid="pro-signup-name"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">{t('email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        data-testid="pro-signup-email"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-1.5">
                                <Label htmlFor="birth-year">
                                    {language === 'zh' ? '出生年份（可选）' : 'Birth Year (Optional)'}
                                </Label>
                                <Select 
                                    value={birthYear ? birthYear.toString() : undefined} 
                                    onValueChange={(val) => setBirthYear(val ? parseInt(val) : '')}
                                >
                                    <SelectTrigger data-testid="pro-signup-birth-year">
                                        <SelectValue placeholder={language === 'zh' ? '选择出生年份' : 'Select birth year'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        {years.map((year) => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {calculatedAnimal && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                        <span className="font-chinese">{calculatedAnimal.chinese}</span>
                                        <span>{calculatedAnimal.english}</span>
                                        <span className="text-xs">({language === 'zh' ? '您的生肖' : 'Your zodiac'})</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="password">{t('password')}</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            data-testid="pro-signup-password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                                    <Input
                                        id="confirmPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        data-testid="pro-signup-confirm-password"
                                    />
                                </div>
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full py-5 text-base font-semibold rounded-full mt-2" 
                                disabled={loading}
                                data-testid="pro-signup-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'zh' ? '创建中...' : 'Creating account...'}
                                    </>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        Sign Up & Pay $1 Trial
                                        <ArrowRight className="w-4 h-4" />
                                    </span>
                                )}
                            </Button>
                            
                            <p className="text-center text-xs text-muted-foreground">
                                Then $47/month after 30 days. Cancel anytime.
                            </p>
                        </form>
                        
                        <div className="mt-5 text-center text-sm">
                            <span className="text-muted-foreground">{t('haveAccount')}</span>{' '}
                            <Link to="/login" className="text-primary hover:underline font-medium">
                                {t('signIn')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ProSignup;
